// features/workstations/dashboard-sections/kill-switch.js
// ═══════════════════════════════════════════════════════════
// KILL SWITCH — Tier B safety widget
// ═══════════════════════════════════════════════════════════
// Locks new entries when any of:
//   - N consecutive losses today
//   - Daily P&L loss limit (USD)
//   - Daily R loss limit
//   - Manual arm
//
// Override requires a typed reason (min 6 chars). Reason is
// logged so the Weekly Review can surface override frequency.
//
// This widget SHOWS the state; enforcement (blocking /trade)
// lands in a separate batch when the trade handler reads from
// this endpoint pre-entry.
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import {
  Eyebrow,
  SerifHeadline,
  SerifSummary,
  SectionStrip,
  PillRow,
  PillTable,
  PractitionerLens,
} from '../../../components/widgets/primitives';

const TRIGGER_LABELS = {
  manually_armed: 'Manually armed',
  triggered_consecutive_losses: 'Consecutive losses limit hit',
  triggered_daily_loss_usd: 'Daily dollar loss limit hit',
  triggered_daily_loss_r: 'Daily R loss limit hit',
};

// ═══════════════════════════════════════════════════════════
// SECTION
// ═══════════════════════════════════════════════════════════
export default function KillSwitchSection({ workstation, userId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editingThresholds, setEditingThresholds] = useState(false);
  const [overriding, setOverriding] = useState(false);

  const load = useCallback(async () => {
    if (!workstation?.id || !userId) return;
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({
        workstation_id: workstation.id,
        user_id: userId,
      });
      const res = await fetch(`/api/workstations/kill-switch?${qs}`, { cache: 'no-store' });
      const payload = res.ok ? await res.json() : null;
      if (!res.ok) setError(payload?.error || `HTTP ${res.status}`);
      setData(payload || null);
    } catch (e) {
      setError(e?.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [workstation?.id, userId]);

  useEffect(() => { load(); }, [load]);

  const postAction = useCallback(async (action, body = {}) => {
    if (!workstation?.id || !userId) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/workstations/kill-switch?action=${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workstation_id: workstation.id,
          user_id: userId,
          ...body,
        }),
      });
      const payload = await res.json();
      if (!res.ok) {
        setError(payload?.error || `HTTP ${res.status}`);
        return false;
      }
      await load();
      return true;
    } catch (e) {
      setError(e?.message || 'Action failed');
      return false;
    } finally {
      setSaving(false);
    }
  }, [workstation?.id, userId, load]);

  const patchConfig = useCallback(async (patch) => {
    if (!workstation?.id || !userId) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/workstations/kill-switch', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workstation_id: workstation.id,
          user_id: userId,
          patch,
        }),
      });
      const payload = await res.json();
      if (!res.ok) {
        setError(payload?.error || `HTTP ${res.status}`);
        return false;
      }
      await load();
      return true;
    } catch (e) {
      setError(e?.message || 'Save failed');
      return false;
    } finally {
      setSaving(false);
    }
  }, [workstation?.id, userId, load]);

  return (
    <section className="ws-killswitch">
      {loading && <div className="ws-killswitch-loading">Loading…</div>}

      {!loading && data && (
        <KillSwitchBody
          data={data}
          saving={saving}
          error={error}
          editingThresholds={editingThresholds}
          setEditingThresholds={setEditingThresholds}
          overriding={overriding}
          setOverriding={setOverriding}
          onArm={() => postAction('arm')}
          onDisarm={() => postAction('disarm')}
          onOverride={(reason, minutes) => postAction('override', { reason, minutes }).then((ok) => {
            if (ok) setOverriding(false);
          })}
          onPatchConfig={(patch) => patchConfig(patch).then((ok) => {
            if (ok) setEditingThresholds(false);
          })}
        />
      )}

      {!loading && !data && error && (
        <div className="ws-killswitch-error">{error}</div>
      )}
    </section>
  );
}

// ═══════════════════════════════════════════════════════════
// BODY
// ═══════════════════════════════════════════════════════════
function KillSwitchBody({
  data, saving, error,
  editingThresholds, setEditingThresholds,
  overriding, setOverriding,
  onArm, onDisarm, onOverride, onPatchConfig,
}) {
  const config = data.config || {};
  const state = data.evaluated_state || {};

  const isLocked = state.is_locked;
  const isOverride = state.is_override_active;
  const tone = isLocked ? 'terra'
    : isOverride ? 'amber'
    : state.is_triggered ? 'amber'
    : 'sage';

  const headline = isLocked
    ? 'Locked — no new entries.'
    : isOverride
      ? 'Override active — be deliberate.'
      : state.is_triggered
        ? `Triggered: ${TRIGGER_LABELS[state.trigger_kind] || state.trigger_kind}.`
        : 'Armed and clear.';

  const summary = isLocked
    ? 'A threshold fired today. Either wait it out, override with a reason, or disarm if the threshold is wrong.'
    : isOverride
      ? `Override expires ${fmtUntil(config.override_active_until)}. Reason logged to the events trail.`
      : state.is_triggered
        ? 'Manually armed. New entries blocked until you disarm.'
        : 'Thresholds set; nothing has fired today. The switch will flip automatically if conditions trip.';

  return (
    <>
      <Eyebrow parts={[
        'kill switch',
        isLocked ? 'LOCKED' : isOverride ? 'override' : state.is_triggered ? 'armed' : 'clear',
      ]} />

      <SerifHeadline>
        <span className={`ws-killswitch-tone-${tone}`}>{headline}</span>
      </SerifHeadline>
      <SerifSummary>{summary}</SerifSummary>

      {error && <div className="ws-killswitch-error">{error}</div>}

      {/* Today's status */}
      <SectionStrip label="today">
        <PillTable>
          <PillRow
            keyText="Trades closed"
            valueText={String(state.today_trade_count || 0)}
            tone="steel"
          />
          <PillRow
            keyText="Consecutive losses (tail)"
            valueText={String(state.consecutive_losses_today || 0)}
            tone={(state.consecutive_losses_today || 0) > 0 ? 'terra' : 'steel'}
          />
          <PillRow
            keyText="P&L today"
            valueText={fmtUsd(state.today_pnl_usd)}
            tone={(state.today_pnl_usd || 0) > 0 ? 'sage' : (state.today_pnl_usd || 0) < 0 ? 'terra' : 'steel'}
          />
          <PillRow
            keyText="R today"
            valueText={fmtR(state.today_total_r)}
            tone={(state.today_total_r || 0) > 0 ? 'sage' : (state.today_total_r || 0) < 0 ? 'terra' : 'steel'}
          />
        </PillTable>
      </SectionStrip>

      {/* Thresholds */}
      <SectionStrip label="thresholds">
        {editingThresholds ? (
          <ThresholdsEditor
            config={config}
            saving={saving}
            onSave={onPatchConfig}
            onCancel={() => setEditingThresholds(false)}
          />
        ) : (
          <>
            <PillTable>
              <PillRow
                keyText="Max consecutive losses"
                valueText={config.max_consecutive_losses ?? '— (disabled)'}
                tone={config.max_consecutive_losses ? 'gold' : 'steel'}
              />
              <PillRow
                keyText="Max daily loss"
                valueText={config.max_daily_loss_usd != null ? fmtUsd(config.max_daily_loss_usd) : '— (disabled)'}
                tone={config.max_daily_loss_usd != null ? 'gold' : 'steel'}
              />
              <PillRow
                keyText="Max daily R loss"
                valueText={config.max_daily_loss_r != null ? fmtR(config.max_daily_loss_r) : '— (disabled)'}
                tone={config.max_daily_loss_r != null ? 'gold' : 'steel'}
              />
            </PillTable>
            <div className="ws-killswitch-actions">
              <button
                type="button"
                className="ws-killswitch-btn ws-killswitch-btn-steel"
                onClick={() => setEditingThresholds(true)}
                disabled={saving}
              >
                edit thresholds
              </button>
            </div>
          </>
        )}
      </SectionStrip>

      {/* Action row — arm/disarm + override */}
      <SectionStrip label="controls">
        <div className="ws-killswitch-actions">
          {config.is_manually_armed ? (
            <button
              type="button"
              className="ws-killswitch-btn ws-killswitch-btn-sage"
              onClick={onDisarm}
              disabled={saving}
            >
              disarm
            </button>
          ) : (
            <button
              type="button"
              className="ws-killswitch-btn ws-killswitch-btn-terra"
              onClick={onArm}
              disabled={saving}
            >
              arm (lock new entries)
            </button>
          )}
          {isLocked && !overriding && (
            <button
              type="button"
              className="ws-killswitch-btn ws-killswitch-btn-amber"
              onClick={() => setOverriding(true)}
              disabled={saving}
            >
              override…
            </button>
          )}
        </div>

        {overriding && (
          <OverrideForm
            saving={saving}
            onSubmit={onOverride}
            onCancel={() => setOverriding(false)}
          />
        )}
      </SectionStrip>

      <PractitionerLens label="how this helps">
        The switch flips automatically when your thresholds hit. Override
        is intentional friction — you have to type the reason. That
        reason shows up in the Weekly Review, so a pattern of overrides
        becomes visible to you before it becomes a habit.
      </PractitionerLens>
    </>
  );
}

// ═══════════════════════════════════════════════════════════
// THRESHOLDS EDITOR
// ═══════════════════════════════════════════════════════════
function ThresholdsEditor({ config, saving, onSave, onCancel }) {
  const [consec, setConsec]   = useState(config.max_consecutive_losses ?? '');
  const [lossUsd, setLossUsd] = useState(config.max_daily_loss_usd ?? '');
  const [lossR, setLossR]     = useState(config.max_daily_loss_r ?? '');

  const submit = (e) => {
    e.preventDefault();
    const patch = {};
    patch.max_consecutive_losses = consec === '' ? null : Math.max(1, Math.floor(Number(consec)));
    // loss limits should be negative
    if (lossUsd === '') patch.max_daily_loss_usd = null;
    else {
      const v = Number(lossUsd);
      patch.max_daily_loss_usd = Number.isFinite(v) ? -Math.abs(v) : null;
    }
    if (lossR === '') patch.max_daily_loss_r = null;
    else {
      const v = Number(lossR);
      patch.max_daily_loss_r = Number.isFinite(v) ? -Math.abs(v) : null;
    }
    onSave(patch);
  };

  return (
    <form className="ws-killswitch-editor" onSubmit={submit}>
      <label className="ws-killswitch-field">
        <span className="ws-killswitch-field-label">consecutive losses (count)</span>
        <input
          type="number"
          min="1"
          step="1"
          className="ws-killswitch-input"
          placeholder="blank to disable"
          value={consec}
          onChange={(e) => setConsec(e.target.value)}
        />
      </label>
      <label className="ws-killswitch-field">
        <span className="ws-killswitch-field-label">max daily loss ($ — absolute)</span>
        <input
          type="number"
          step="50"
          min="0"
          className="ws-killswitch-input"
          placeholder="blank to disable"
          value={lossUsd === '' ? '' : Math.abs(lossUsd)}
          onChange={(e) => setLossUsd(e.target.value)}
        />
      </label>
      <label className="ws-killswitch-field">
        <span className="ws-killswitch-field-label">max daily R loss (absolute)</span>
        <input
          type="number"
          step="0.5"
          min="0"
          className="ws-killswitch-input"
          placeholder="blank to disable"
          value={lossR === '' ? '' : Math.abs(lossR)}
          onChange={(e) => setLossR(e.target.value)}
        />
      </label>
      <div className="ws-killswitch-actions">
        <button type="submit" disabled={saving} className="ws-killswitch-btn ws-killswitch-btn-sage">
          {saving ? 'saving…' : 'save'}
        </button>
        <button type="button" onClick={onCancel} disabled={saving} className="ws-killswitch-btn ws-killswitch-btn-steel">
          cancel
        </button>
      </div>
    </form>
  );
}

// ═══════════════════════════════════════════════════════════
// OVERRIDE FORM
// ═══════════════════════════════════════════════════════════
function OverrideForm({ saving, onSubmit, onCancel }) {
  const [reason, setReason] = useState('');
  const [minutes, setMinutes] = useState('60');

  const submit = (e) => {
    e.preventDefault();
    if (reason.trim().length < 6) return;
    onSubmit(reason.trim(), Number(minutes) || 60);
  };

  const canSubmit = reason.trim().length >= 6 && !saving;

  return (
    <form className="ws-killswitch-override" onSubmit={submit}>
      <label className="ws-killswitch-field">
        <span className="ws-killswitch-field-label">reason (required, min 6 chars)</span>
        <textarea
          className="ws-killswitch-textarea"
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why are you overriding the lock? This is logged."
          autoFocus
        />
      </label>
      <label className="ws-killswitch-field">
        <span className="ws-killswitch-field-label">duration (minutes, 5–480)</span>
        <input
          type="number"
          min="5"
          max="480"
          step="5"
          className="ws-killswitch-input"
          value={minutes}
          onChange={(e) => setMinutes(e.target.value)}
        />
      </label>
      <div className="ws-killswitch-actions">
        <button type="submit" disabled={!canSubmit} className="ws-killswitch-btn ws-killswitch-btn-amber">
          {saving ? 'overriding…' : 'override'}
        </button>
        <button type="button" onClick={onCancel} disabled={saving} className="ws-killswitch-btn ws-killswitch-btn-steel">
          cancel
        </button>
      </div>
    </form>
  );
}

// ─── Formatters ───
function fmtUsd(n) {
  if (n == null || Number.isNaN(n)) return '—';
  const sign = n > 0 ? '+' : n < 0 ? '−' : '';
  const abs = Math.abs(n);
  if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(1)}k`;
  return `${sign}$${abs.toFixed(0)}`;
}
function fmtR(n) {
  if (n == null || Number.isNaN(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${Number(n).toFixed(1)}R`;
}
function fmtUntil(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const min = Math.max(0, Math.round((d.getTime() - Date.now()) / 60000));
    if (min === 0) return 'now';
    if (min < 60) return `in ${min} min`;
    const hours = Math.floor(min / 60);
    const rem = min % 60;
    return `in ${hours}h${rem ? ` ${rem}m` : ''}`;
  } catch {
    return '';
  }
}
